import React, { useState, useMemo, useEffect } from "react";
import { useMeeting } from "@videosdk.live/react-sdk";
import { useBreakoutRooms } from "../../context/BreakoutRoomsContext";

export default function BreakoutRoomsModal({ isOpen, onClose, meetingRole }) {
  console.log("🔵 BreakoutRoomsModal: Component RENDERED, isOpen =", isOpen, "meetingRole =", meetingRole);
  
  // All hooks must be called unconditionally before any early returns
  const mMeeting = useMeeting();
  const participants = mMeeting?.participants || new Map();
  const localParticipantId = mMeeting?.localParticipant?.id;
  console.log("🔵 BreakoutRoomsModal: mMeeting exists =", !!mMeeting, "participants count =", participants.size);

  // Hook must be called unconditionally
  const {
    rooms,
    participantRooms,
    joinRequests,
    createRoom,
    deleteRoom,
    addParticipantToRoom,
    removeParticipantFromRoom,
    acceptJoinRequest,
    rejectJoinRequest,
    currentRoom,
  } = useBreakoutRooms();
  
  console.log("🔵 BreakoutRoomsModal: BreakoutRooms context loaded, rooms count =", Object.keys(rooms || {}).length);

  const [newRoomName, setNewRoomName] = useState("");
  const [selectedRoomForParticipant, setSelectedRoomForParticipant] = useState(null);
  const [participantToAssign, setParticipantToAssign] = useState(null);

  const isHost = ["moderator", "co-host"].includes(meetingRole);

  const roomList = useMemo(() => Object.values(rooms), [rooms]);

  const mainRoomParticipants = useMemo(() => {
    const allParticipantIds = Array.from(participants.keys());
    return allParticipantIds.filter((id) => !participantRooms[id]);
  }, [participants, participantRooms]);

  useEffect(() => {
    console.log("🟢 BreakoutRoomsModal: isOpen changed to", isOpen);
    console.log("🟢 BreakoutRoomsModal: rooms count =", Object.keys(rooms).length);
    if (isOpen) {
      console.log("🟢 BreakoutRoomsModal: Modal SHOULD BE VISIBLE NOW!");
      console.log("🟢 BreakoutRoomsModal: Current room =", currentRoom);
    }
  }, [isOpen, rooms, currentRoom]);

  // Early return after all hooks
  if (!isOpen) {
    console.log("🔴 BreakoutRoomsModal: isOpen is false, returning null");
    return null;
  }

  console.log("✅ BreakoutRoomsModal: About to RENDER MODAL CONTENT - isOpen =", isOpen);

  const handleCreateRoom = () => {
    if (newRoomName.trim()) {
      createRoom(newRoomName.trim());
      setNewRoomName("");
    }
  };

  const handleAddParticipant = (roomId, participantId) => {
    addParticipantToRoom(roomId, participantId);
    setSelectedRoomForParticipant(null);
    setParticipantToAssign(null);
  };

  const handleRemoveParticipant = (roomId, participantId) => {
    removeParticipantFromRoom(roomId, participantId);
  };

  const handleAcceptRequest = (roomId, participantId) => {
    acceptJoinRequest(roomId, participantId);
  };

  const handleRejectRequest = (roomId, participantId) => {
    rejectJoinRequest(roomId, participantId);
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75" 
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-[#060621] rounded-lg shadow-xl border border-gray-600 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Breakout Rooms</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Create Room Section - Host Only */}
          {isHost && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white mb-4">Create New Room</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateRoom();
                    }
                  }}
                  placeholder="Enter room name"
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-[#7700ff]"
                />
                <button
                  onClick={handleCreateRoom}
                  className="px-6 py-2 bg-[#7700ff] text-white rounded hover:bg-[#6600dd] transition-colors"
                >
                  Create
                </button>
              </div>
            </div>
          )}

          {/* Join Requests Section - Host Only */}
          {isHost && Object.keys(joinRequests).length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white mb-4">Join Requests</h3>
              <div className="space-y-2">
                {Object.entries(joinRequests).map(([roomId, requests]) => {
                  const room = rooms[roomId];
                  if (!room || requests.length === 0) return null;

                  return (
                    <div key={roomId} className="border border-gray-700 rounded p-3">
                      <div className="text-sm font-medium text-gray-300 mb-2">
                        Request to join: {room.name}
                      </div>
                      {requests.map((request) => {
                        const participant = participants.get(request.participantId);
                        const participantName = participant?.displayName || request.participantName || "Participant";
                        return (
                          <div
                            key={request.participantId}
                            className="flex items-center justify-between bg-gray-700 rounded p-2 mb-2"
                          >
                            <span className="text-white text-sm">{participantName}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAcceptRequest(roomId, request.participantId)}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleRejectRequest(roomId, request.participantId)}
                                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rooms List */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Rooms</h3>
            {roomList.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No breakout rooms created yet
              </div>
            ) : (
              roomList.map((room) => {
                const roomParticipants = room.participants || [];
                const roomRequestCount = joinRequests[room.id]?.length || 0;

                return (
                  <div
                    key={room.id}
                    className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg font-medium text-white">{room.name}</h4>
                        <span className="text-sm text-gray-400">
                          {roomParticipants.length} participant{roomParticipants.length !== 1 ? "s" : ""}
                        </span>
                        {roomRequestCount > 0 && isHost && (
                          <span className="px-2 py-1 bg-yellow-600 text-white rounded text-xs">
                            {roomRequestCount} request{roomRequestCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {isHost && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete "${room.name}"?`)) {
                              deleteRoom(room.id);
                            }
                          }}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          Delete Room
                        </button>
                      )}
                    </div>

                    {/* Participants in Room */}
                    <div className="space-y-2 mb-4">
                      {roomParticipants.length === 0 ? (
                        <div className="text-sm text-gray-400 italic">No participants yet</div>
                      ) : (
                        roomParticipants.map((participantId) => {
                          const participant = participants.get(participantId);
                          const participantName = participant?.displayName || "Participant";
                          const isLocal = participantId === localParticipantId;

                          return (
                            <div
                              key={participantId}
                              className="flex items-center justify-between bg-gray-700 rounded p-2"
                            >
                              <span className="text-white text-sm">
                                {participantName} {isLocal && "(You)"}
                              </span>
                              {isHost && (
                                <button
                                  onClick={() => handleRemoveParticipant(room.id, participantId)}
                                  className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Add Participant Section - Host Only */}
                    {isHost && (
                      <div className="border-t border-gray-700 pt-4 mt-4">
                        <div className="flex gap-2 items-center">
                          <select
                            value={participantToAssign || ""}
                            onChange={(e) => {
                              setParticipantToAssign(e.target.value);
                              setSelectedRoomForParticipant(room.id);
                            }}
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-[#7700ff]"
                          >
                            <option value="">Select participant to add...</option>
                            {Array.from(participants.entries()).map(([id, participant]) => {
                              // Don't show participants already in a room
                              if (participantRooms[id]) return null;
                              return (
                                <option key={id} value={id}>
                                  {participant.displayName || "Participant"}
                                </option>
                              );
                            })}
                          </select>
                          <button
                            onClick={() => {
                              if (participantToAssign && selectedRoomForParticipant === room.id) {
                                handleAddParticipant(room.id, participantToAssign);
                              }
                            }}
                            disabled={!participantToAssign || selectedRoomForParticipant !== room.id}
                            className="px-4 py-2 bg-[#7700ff] text-white rounded text-sm hover:bg-[#6600dd] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Main Room (Unassigned Participants) */}
          {isHost && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-medium text-white mb-4">Main Meeting</h3>
              <div className="space-y-2">
                {mainRoomParticipants.length === 0 ? (
                  <div className="text-sm text-gray-400 italic">All participants are in rooms</div>
                ) : (
                  mainRoomParticipants.map((participantId) => {
                    const participant = participants.get(participantId);
                    const participantName = participant?.displayName || "Participant";

                    return (
                      <div
                        key={participantId}
                        className="flex items-center justify-between bg-gray-700 rounded p-2"
                      >
                        <span className="text-white text-sm">{participantName}</span>
                        <select
                          value=""
                          onChange={(e) => {
                            const roomId = e.target.value;
                            if (roomId) {
                              handleAddParticipant(roomId, participantId);
                            }
                          }}
                          className="px-3 py-1 bg-gray-600 border border-gray-500 rounded text-white text-xs focus:outline-none focus:border-[#7700ff]"
                        >
                          <option value="">Assign to room...</option>
                          {roomList.map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
